import React, { useContext, useState } from "react";
import CustomButton from "../../../components/Button/CustomButton";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import ConfidentialConfirmationDialog from "../../../components/Dialog/ConfidentialConfirmationDialog";
import { tesaBlue } from "../../../styles/colors";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import useEnv from "../../../hooks/env";
import { EditRequestNameDialogProps } from "../../Dashboard/EditRequestNameDialog";
import TestRequestLifecycleApi from "../../../api/services/TestRequestLifecycleApi";
import TestRequestResponse from "../../../api/models/test-request/TestRequestResponse";

export interface SubmitTestRequestButtonProps {
  disabled?: boolean;
  requestTitlePrefix: string;
  requestTitleSuffix: string;
  style: React.CSSProperties;
  handleShowSuccessMessage: () => void;
  highConfidentialityRequired: () => Promise<boolean | void>;
  onSuccess: (testRequest: TestRequestResponse) => void | Promise<unknown>;
  onSubmitClick: (setHighlyConfidential: boolean, newRequestTitle?: string) => Promise<TestRequestResponse | void>;
  showEditRequestNameDialog: (doShow: boolean, props?: EditRequestNameDialogProps) => Promise<string | void>;
}

const SubmitTestRequestButton: React.FC<SubmitTestRequestButtonProps> = (props) => {
  const {
    disabled,
    handleShowSuccessMessage,
    highConfidentialityRequired,
    onSubmitClick,
    onSuccess,
    showEditRequestNameDialog,
    requestTitlePrefix,
    requestTitleSuffix,
    style,
  } = props;
  const { api } = useEnv();
  const testRequestLifecycleApi = new TestRequestLifecycleApi(api);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const fullTitle = `${requestTitlePrefix || ""} ${requestTitleSuffix || ""}`;

  const [showConfidentialDialog, setShowConfidentialDialog] = useState(false);
  const [pendingNewRequestTitle, setPendingNewRequestTitle] = useState("");

  const setSubmissionStatus = (id: string) => {
    testRequestLifecycleApi
      .submitTestRequest(id)
      .then((value) => {
        handleShowSuccessMessage();
        return value.data;
      })
      .then(onSuccess)
      .catch(handleHttpError);
  };

  const handleProceedConfidential = () => {
    setShowConfidentialDialog(false);
    // BE VALIDATION & TR PUT REQ TO SAVE with highly confidential flag
    onSubmitClick(true, pendingNewRequestTitle)
      .then((res) => {
        if (res && res?.requestId) {
          // SUBMIT TO SUBMITTED STATUS IN SYSTEM
          setSubmissionStatus(res?.requestId);
        }
        setPendingNewRequestTitle("");
      })
      .catch((error) => {
        setPendingNewRequestTitle("");
        handleHttpError(error);
      });
  };

  const handleCancelConfidential = () => {
    setShowConfidentialDialog(false);
    setPendingNewRequestTitle("");
  };

  const onSubmit = () => {
    let newRequestTitle = "";
    showEditRequestNameDialog(!requestTitleSuffix)
      .then((newName) => {
        newRequestTitle = newName || newRequestTitle;
      })
      .then(() =>
        highConfidentialityRequired().then((confirmConfidentiality) => {
          if (confirmConfidentiality) {
            // Show the confidential dialog
            setPendingNewRequestTitle(newRequestTitle);
            setShowConfidentialDialog(true);
          } else {
            // Show regular confirmation dialog if title exists, otherwise proceed directly
            const confirmationPromise: Promise<string | void> = requestTitleSuffix
              ? showConfirmationDialog({
                  text: (
                    <>
                      You are about to submit <b>{fullTitle}</b>.
                    </>
                  ),
                  confirmText: "Submit",
                  confirmColor: tesaBlue,
                })
              : Promise.resolve();

            confirmationPromise.then(() => {
              // BE VALIDATION & TR PUT REQ TO SAVE
              onSubmitClick(false, newRequestTitle).then((res) => {
                if (res && res?.requestId) {
                  // SUBMIT TO SUBMITTED STATUS IN SYSTEM
                  setSubmissionStatus(res?.requestId);
                }
              });
            });
          }
        }),
      )
      .catch(() => {
        // do nothing on dialog close
      });
  };

  return (
    <>
      <CustomButton disabled={disabled} onClick={onSubmit} primary text="Submit" style={style} />
      <ConfidentialConfirmationDialog
        objectName="test request"
        open={showConfidentialDialog}
        onCancel={handleCancelConfidential}
        onProceed={handleProceedConfidential}
      />
    </>
  );
};

export default SubmitTestRequestButton;
